import { jsPDF } from 'jspdf';

export const generateInvoicePDF = (invoiceData) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [37, 99, 235]; // Blue-600
  const textColor = [15, 23, 42]; // Slate-900
  const secondaryColor = [100, 116, 139]; // Slate-500
  const accentColor = [34, 197, 94]; // Green-600
  
  // Helper functions
  const addText = (text, x, y, options = {}) => {
    const { fontSize = 12, color = textColor, align = 'left', bold = false } = options;
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    if (bold) doc.setFont(undefined, 'bold');
    else doc.setFont(undefined, 'normal');
    doc.text(text, x, y, { align });
  };

  const addLine = (x1, y1, x2, y2, color = secondaryColor) => {
    doc.setDrawColor(...color);
    doc.line(x1, y1, x2, y2);
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Header Section
  let yPosition = 20;
  
  // Business Logo/Brand
  doc.setFillColor(...primaryColor);
  doc.rect(15, 15, 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('QuoteSnapp', 30, 23, { align: 'center' });
  
  // INVOICE Title
  addText('INVOICE', 195, 23, { fontSize: 32, color: primaryColor, bold: true, align: 'right' });
  
  yPosition = 45;
  
  // Invoice Info (Right side)
  addText(`Invoice #: ${invoiceData.invoice_number}`, 195, yPosition, { align: 'right', bold: true });
  yPosition += 6;
  addText(`Date: ${formatDate(invoiceData.invoice_date)}`, 195, yPosition, { align: 'right', fontSize: 10, color: secondaryColor });
  yPosition += 5;
  addText(`Due: ${formatDate(invoiceData.due_date)}`, 195, yPosition, { align: 'right', fontSize: 10, color: secondaryColor });
  
  yPosition = 45;
  
  // From Section (Left side)
  addText('FROM:', 15, yPosition, { fontSize: 10, color: secondaryColor, bold: true });
  yPosition += 6;
  addText(invoiceData.business_name || 'Your Business', 15, yPosition, { fontSize: 12, bold: true });
  yPosition += 5;
  if (invoiceData.business_contact) {
    addText(invoiceData.business_contact, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  
  yPosition += 10;
  
  // Bill To Section
  addText('BILL TO:', 15, yPosition, { fontSize: 10, color: secondaryColor, bold: true });
  yPosition += 6;
  addText(invoiceData.client_name, 15, yPosition, { fontSize: 12, bold: true });
  yPosition += 5;
  if (invoiceData.client_email) {
    addText(invoiceData.client_email, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  if (invoiceData.client_phone) {
    addText(invoiceData.client_phone, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  
  yPosition += 15;
  
  // Line Items Table
  addText('DESCRIPTION', 15, yPosition, { fontSize: 10, bold: true });
  addText('QTY', 120, yPosition, { fontSize: 10, bold: true, align: 'right' });
  addText('RATE', 150, yPosition, { fontSize: 10, bold: true, align: 'right' });
  addText('AMOUNT', 195, yPosition, { fontSize: 10, bold: true, align: 'right' });
  yPosition += 3;
  addLine(15, yPosition, 195, yPosition, primaryColor);
  yPosition += 8;
  
  // Line items
  invoiceData.line_items.forEach((item) => {
    addText(item.description, 15, yPosition, { fontSize: 10 });
    addText(item.quantity.toString(), 120, yPosition, { fontSize: 10, align: 'right' });
    addText(formatCurrency(item.unit_price), 150, yPosition, { fontSize: 10, align: 'right' });
    addText(formatCurrency(item.total), 195, yPosition, { fontSize: 10, align: 'right', bold: true });
    yPosition += 6;
  });
  
  yPosition += 5;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 10;
  
  // Subtotal
  addText('Subtotal:', 150, yPosition, { fontSize: 11, align: 'right' });
  addText(formatCurrency(invoiceData.subtotal), 195, yPosition, { fontSize: 11, align: 'right' });
  yPosition += 6;
  
  // Tax (if applicable)
  if (invoiceData.tax_rate > 0) {
    addText(`Tax (${invoiceData.tax_rate}%):`, 150, yPosition, { fontSize: 11, align: 'right' });
    addText(formatCurrency(invoiceData.tax_amount), 195, yPosition, { fontSize: 11, align: 'right' });
    yPosition += 6;
  }
  
  yPosition += 3;
  addLine(150, yPosition, 195, yPosition, primaryColor);
  yPosition += 8;
  
  // Total (highlighted)
  doc.setFillColor(240, 249, 255); // Light blue background
  doc.rect(148, yPosition - 6, 49, 10, 'F');
  
  addText('TOTAL DUE:', 150, yPosition, { fontSize: 14, bold: true, align: 'right' });
  addText(formatCurrency(invoiceData.total), 195, yPosition, { fontSize: 16, color: accentColor, bold: true, align: 'right' });
  yPosition += 15;
  
  // Payment Terms
  addLine(15, yPosition, 195, yPosition);
  yPosition += 7;
  addText('PAYMENT TERMS', 15, yPosition, { fontSize: 10, color: secondaryColor, bold: true });
  yPosition += 6;
  addText(invoiceData.payment_terms, 15, yPosition, { fontSize: 10 });
  yPosition += 10;
  
  // Notes (if any)
  if (invoiceData.notes) {
    addText('NOTES', 15, yPosition, { fontSize: 10, color: secondaryColor, bold: true });
    yPosition += 6;
    const noteLines = doc.splitTextToSize(invoiceData.notes, 180);
    noteLines.forEach(line => {
      addText(line, 15, yPosition, { fontSize: 9, color: secondaryColor });
      yPosition += 5;
    });
  }
  
  // Footer
  yPosition = 280;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 5;
  addText('Thank you for your business!', 105, yPosition, { fontSize: 10, align: 'center', bold: true });
  addText(`Invoice generated by QuoteSnapp • ${formatDate(new Date())}`, 105, yPosition + 4, { 
    fontSize: 8, 
    color: secondaryColor, 
    align: 'center' 
  });
  
  // Generate filename
  const customerSlug = invoiceData.client_name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const filename = `invoice-${invoiceData.invoice_number}-${customerSlug}.pdf`;
  
  // Save the PDF
  doc.save(filename);
  
  return filename;
};
